import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Link2, AlertTriangle } from "lucide-react";

/* ---------------------------------------------------------------------------
 * Rename-Slug dialog: changes a pub's `slug` (its landing URL segment) and
 * cascades the update to every denormalised copy stored elsewhere. Backend
 * `/api/admin/rename-pub-slug` handles the dry-run + actual rename (see
 * admin.py). We show the dry-run plan first (how many users get their
 * `client_slug` updated, which URLs break) so the admin can eyeball the
 * impact before confirming.
 * ------------------------------------------------------------------------- */

// Live-normalise as the user types, matching the backend regex
// (lowercase letters, digits, single-dash separators).
const normaliseSlugInput = (raw) =>
  (raw || "")
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+/, "");

export function RenameSlugButton({ pub, onRenamed }) {
  const [open, setOpen] = useState(false);
  const [newSlug, setNewSlug] = useState("");
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setPlan(null);
    setNewSlug("");
  };

  const preview = async () => {
    const cleaned = normaliseSlugInput(newSlug).replace(/-+$/, "");
    if (!cleaned || cleaned.length < 2) {
      toast.error("Enter a new slug (2+ characters).");
      return;
    }
    if (cleaned === pub.slug) {
      toast.error("New slug matches the current slug — nothing to change.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/rename-pub-slug", {
        pub_id: pub.id,
        new_slug: cleaned,
        dry_run: true,
      });
      setPlan(data.plan);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/rename-pub-slug", {
        pub_id: pub.id,
        new_slug: plan.pub.new_slug,
        dry_run: false,
      });
      toast.success(
        `Slug renamed to /${data.result.new_slug}. Updated ${data.result.users_updated} user(s).`,
      );
      setOpen(false);
      reset();
      onRenamed?.(data.result.new_slug);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="rename-slug-open"
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-700 hover:text-zinc-950 border border-zinc-300 hover:border-zinc-950 rounded-sm px-3 h-10"
      >
        <Link2 className="h-3.5 w-3.5" /> Rename URL slug…
      </button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="bg-white max-w-lg" data-testid="rename-slug-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold tracking-tight text-zinc-950">
              Rename the URL slug for {pub.name}
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-500">
              Changes the landing URL segment. The pub keeps its id, prize,
              sponsors and every prediction — only the slug changes.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
                Current slug
              </Label>
              <p
                className="mt-1 font-mono text-sm text-zinc-500"
                data-testid="rename-slug-current"
              >
                /{pub.slug}
              </p>
            </div>

            <div>
              <Label
                htmlFor="rename-slug-new"
                className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500"
              >
                New slug
              </Label>
              <div className="mt-2 relative">
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-zinc-400 select-none"
                >
                  /
                </span>
                <Input
                  id="rename-slug-new"
                  data-testid="rename-slug-input"
                  value={newSlug}
                  onChange={(e) => {
                    setNewSlug(normaliseSlugInput(e.target.value));
                    setPlan(null);
                  }}
                  placeholder="hq-salford"
                  autoComplete="off"
                  spellCheck={false}
                  className="h-10 pl-6 rounded-sm border-zinc-300 font-mono text-sm"
                />
              </div>
              <p className="mt-1 text-[11px] text-zinc-500">
                Lowercase letters, digits and dashes only. No leading, trailing or double dashes.
              </p>
            </div>

            {plan && (
              <div
                className="rounded-sm border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 space-y-2"
                data-testid="rename-slug-plan"
              >
                <p className="font-semibold">Dry run — this is what will happen:</p>
                <ul className="list-disc ml-4 space-y-1">
                  <li>
                    Pub slug changes from <span className="font-mono font-semibold">/{plan.pub.old_slug}</span> → <span className="font-mono font-semibold">/{plan.pub.new_slug}</span>.
                  </li>
                  <li>
                    <span className="font-semibold">{plan.users_with_old_slug}</span> user(s) will have their <code>client_slug</code> updated.
                  </li>
                </ul>
                <div className="mt-2 flex gap-2 items-start rounded-sm border border-red-300 bg-red-50 p-2 text-red-900">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
                  <div>
                    <p className="font-semibold">Existing links will break.</p>
                    <p className="mt-0.5">
                      Any printed QR codes, WhatsApp invites and browser bookmarks referencing the old slug will 404. Send fresh links after renaming.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => { setOpen(false); reset(); }}
              data-testid="rename-slug-cancel"
              className="h-10 rounded-sm"
            >
              Cancel
            </Button>
            {!plan ? (
              <Button
                onClick={preview}
                disabled={!newSlug || busy}
                data-testid="rename-slug-preview"
                className="h-10 rounded-sm bg-zinc-950 hover:bg-black text-white"
              >
                {busy ? "Checking…" : "Preview rename"}
              </Button>
            ) : (
              <Button
                onClick={confirm}
                disabled={busy}
                data-testid="rename-slug-confirm"
                className="h-10 rounded-sm bg-red-600 hover:bg-red-700 text-white"
              >
                {busy ? "Renaming…" : "Confirm rename"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
